export const STEP_DEFS = [
  { key: "basics", label: "Game basics" },
  { key: "locations", label: "Setting & locations" },
  { key: "rounds", label: "Rounds & content" },
  { key: "review", label: "Review content" },
  { key: "teams", label: "Teams" },
  { key: "routes", label: "Route assignment" },
  { key: "publish", label: "Publish" },
];

export function stepNumberFor(key) {
  return STEP_DEFS.findIndex((s) => s.key === key) + 1;
}

/** 1-based step number of the "review" step, used to gate Continue until every round is approved. */
export const REVIEW_STEP_NUMBER = stepNumberFor("review");

/** 1-based step number of the "rounds" step, used to gate Continue until content is generated. */
export const ROUNDS_STEP_NUMBER = stepNumberFor("rounds");

// Generated content must actually fit the round's CURRENT answer type — switching a round from
// text to multiple-choice (or vice versa) after generating leaves the old content shaped for
// the old type (no options, or an acceptedAnswer that no longer means anything), so it needs
// regenerating even though a `question` is already saved.
export function isContentValidForType(rc, answerType) {
  if (!rc?.question) return false;
  if (answerType === "mcq") {
    return Array.isArray(rc.options) && rc.options.filter((o) => String(o || "").trim()).length >= 2 && rc.correctOption != null;
  }
  if (answerType === "text" || answerType === "numeric") {
    return String(rc.acceptedAnswer ?? "").trim() !== "";
  }
  return true; // info/qrcode/media types only need the question/clue itself
}

export function estimateMinutes(numRounds, setting) {
  const perRound = setting === "outdoor" || setting === "campus" ? 9 : 8;
  const lo = numRounds * (perRound - 1);
  const hi = numRounds * (perRound + 2);
  return [lo, hi];
}

export const SETTING_OPTIONS = [
  { value: "office", label: "Office" },
  { value: "hotel_resort", label: "Hotel / Resort" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "campus", label: "Campus" },
  { value: "outdoor", label: "Outdoor venue" },
  { value: "custom", label: "Custom" },
];

export const ROUND_COUNT_OPTIONS = [4, 5, 6, 8];
export const ROUND_COUNT_MIN = 1;
export const ROUND_COUNT_MAX = 15;

export const ANSWER_TYPE_OPTIONS = [
  { value: "text", label: "Text answer" },
  { value: "numeric", label: "Numeric answer" },
  { value: "mcq", label: "Multiple choice" },
  { value: "qrcode", label: "Scan QR code" },
  { value: "photo", label: "Photo submission" },
  { value: "video", label: "Video submission" },
  { value: "audio", label: "Audio submission" },
  { value: "gif", label: "GIF submission" },
];

/** Types the team answers by uploading/capturing a file — reviewed manually by the host, no
 * automatic text match. */
export const MEDIA_ANSWER_TYPES = ["photo", "video", "audio", "gif"];

export const SCORING_MODEL_OPTIONS = [
  { value: "points_time", label: "Points + time taken" },
  { value: "points_only", label: "Points only" },
  { value: "time_only", label: "Time only" },
];

// Whether revealing a hint costs the team time or points — no separate admin setting for this
// (that used to be its own "Hint penalty type" control, redundant with — and sometimes
// contradicting — the Scoring model). Points-only hunts pay in points; every other scoring model
// (including points_time, which always tracks time as the tiebreaker) pays in minutes.
export function hintPenaltyTypeFor(scoringModel) {
  return scoringModel === "points_only" ? "points" : "time";
}

export const DIFFICULTY_OPTIONS = [
  { value: "easy", label: "Easy" },
  { value: "balanced", label: "Balanced" },
  { value: "hard", label: "Hard" },
];

export const TONE_OPTIONS = [
  { value: "playful", label: "Playful" },
  { value: "corporate", label: "Corporate" },
  { value: "cryptic", label: "Cryptic" },
];

export const TEAM_COUNT_OPTIONS = [2, 4, 6, 8, 10, 12];
export const TEAM_COUNT_MIN = 1;
export const TEAM_COUNT_MAX = 15;

export const JOIN_METHOD_OPTIONS = [
  { value: "shared_link", label: "Shared join link" },
  { value: "per_team_code", label: "Per-team code" },
  { value: "pre_assigned", label: "Pre-assigned" },
];

/** Suggested checkpoints per setting (name + location), used to pre-fill a fresh hunt with 6 starting checkpoints. */
export const DEFAULT_CHECKPOINTS_BY_SETTING = {
  office: [
    { name: "Reception", location: "Ground floor, main entrance" },
    { name: "2nd floor library", location: "Quiet zone, east wing" },
    { name: "Cafeteria", location: "Ground floor" },
    { name: "Recreation room", location: "4th floor" },
    { name: "Terrace garden", location: "8th floor" },
    { name: "Server room", location: "Basement B1" },
  ],
  hotel_resort: [
    { name: "Lobby", location: "Main entrance" },
    { name: "Pool deck", location: "Ground floor, poolside" },
    { name: "Spa", location: "1st floor" },
    { name: "Ballroom", location: "2nd floor, east wing" },
    { name: "Rooftop bar", location: "Top floor" },
    { name: "Concierge desk", location: "Lobby, near main doors" },
  ],
  manufacturing: [
    { name: "Main gate", location: "Front security checkpoint" },
    { name: "Assembly line", location: "Shop floor, bay 2" },
    { name: "Quality lab", location: "1st floor, north wing" },
    { name: "Warehouse", location: "Loading dock" },
    { name: "Cafeteria", location: "Ground floor" },
    { name: "Control room", location: "2nd floor" },
  ],
  campus: [
    { name: "Main quad", location: "Central courtyard" },
    { name: "Library", location: "Block A" },
    { name: "Auditorium", location: "Block B, ground floor" },
    { name: "Cafeteria", location: "Block C" },
    { name: "Sports complex", location: "Behind Block D" },
    { name: "Admin block", location: "Main entrance" },
  ],
  outdoor: [
    { name: "Entry gate", location: "Main entrance" },
    { name: "Fountain", location: "Central plaza" },
    { name: "Amphitheater", location: "East side" },
    { name: "Trail head", location: "North path" },
    { name: "Picnic area", location: "West lawn" },
    { name: "Lookout point", location: "Highest ground" },
  ],
  custom: [
    { name: "Checkpoint 1", location: "" },
    { name: "Checkpoint 2", location: "" },
    { name: "Checkpoint 3", location: "" },
    { name: "Checkpoint 4", location: "" },
    { name: "Checkpoint 5", location: "" },
    { name: "Checkpoint 6", location: "" },
  ],
};

export function makeId(prefix) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function defaultRound(id) {
  return { id, answerType: "text", points: 100, hintPenaltyMin: 2 };
}

/** `options`/`correctOption` only apply to mcq rounds; `acceptedAnswer`/`alsoAccept` only to text
 * rounds; photo rounds use neither — a photo has no text match, it's reviewed manually.
 * `infoMessage` is optional context shown to the team above the answer UI, available on every
 * type (most useful on text/numeric/mcq, which otherwise show only the bare question). */
export function defaultRoundContent(roundId) {
  return {
    roundId,
    clue: "",
    question: "",
    acceptedAnswer: "",
    alsoAccept: "",
    options: ["", "", "", ""],
    correctOption: 0,
    hint: "",
    infoMessage: "",
    approved: false,
  };
}

// Older hunts may still have rounds saved with answerType "info" (a standalone "no answer needed"
// type, since retired in favor of the infoMessage field on any type). Transparently migrate them
// to a text round on load, carrying the old question/clue into infoMessage — they'll show up as
// needing a real accepted answer (see isContentValidForType), which is a deliberate consequence of
// no longer supporting an answer-free round type; the admin has to supply one to keep editing.
export function migrateLegacyInfoRounds(rounds, roundContent) {
  const rs = Array.isArray(rounds) ? rounds : [];
  const rcs = Array.isArray(roundContent) ? roundContent : [];
  let changed = false;
  const nextRounds = rs.map((r) => {
    if (r?.answerType !== "info") return r;
    changed = true;
    return { ...r, answerType: "text" };
  });
  const nextRoundContent = rcs.map((rc, i) => {
    if (rs[i]?.answerType !== "info") return rc;
    changed = true;
    return { ...rc, infoMessage: rc?.infoMessage || rc?.question || rc?.clue || "" };
  });
  return changed ? { rounds: nextRounds, roundContent: nextRoundContent } : { rounds: rs, roundContent: rcs };
}

export const QUESTION_SOURCE_OPTIONS = [
  { value: "ai", label: "AI" },
  { value: "csv", label: "CSV" },
  { value: "manual", label: "Manual" },
];

const ROUND_CONTENT_CSV_COLUMNS = [
  "round",
  "clue",
  "question",
  "acceptedAnswer",
  "alsoAccept",
  "option1",
  "option2",
  "option3",
  "option4",
  "correctOption",
  "hint",
];

function csvField(value) {
  const s = String(value ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Blank fill-in template sized to the current round count, one row per round. */
export function sampleRoundContentCsv(numRounds) {
  const lines = [ROUND_CONTENT_CSV_COLUMNS.join(",")];
  for (let i = 0; i < numRounds; i++) {
    lines.push([i + 1, "", "", "", "", "", "", "", "", "", ""].map(csvField).join(","));
  }
  return lines.join("\r\n");
}

function parseCsvRows(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      field = "";
      if (row.length > 1 || row[0] !== "") rows.push(row);
      row = [];
    } else {
      field += c;
    }
  }
  if (field !== "" || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/** Parses an uploaded CSV (round,clue,question,acceptedAnswer,alsoAccept,option1..4,correctOption,hint)
 * into a roundId-keyed content array matching `rounds`. option1..4/correctOption only matter for
 * mcq rounds; acceptedAnswer/alsoAccept only matter for text rounds. */
export function parseRoundContentCsv(text, rounds) {
  const allRows = parseCsvRows(text).filter((r) => r.some((c) => c.trim() !== ""));
  if (allRows.length === 0) return null;

  const header = allRows[0].map((h) => h.trim().toLowerCase());
  const hasHeader = header.includes("clue") || header.includes("question");
  const dataRows = hasHeader ? allRows.slice(1) : allRows;
  const idx = hasHeader
    ? {
        round: header.indexOf("round"),
        clue: header.indexOf("clue"),
        question: header.indexOf("question"),
        acceptedAnswer: header.indexOf("acceptedanswer"),
        alsoAccept: header.indexOf("alsoaccept"),
        option1: header.indexOf("option1"),
        option2: header.indexOf("option2"),
        option3: header.indexOf("option3"),
        option4: header.indexOf("option4"),
        correctOption: header.indexOf("correctoption"),
        hint: header.indexOf("hint"),
      }
    : {
        round: 0,
        clue: 1,
        question: 2,
        acceptedAnswer: 3,
        alsoAccept: 4,
        option1: 5,
        option2: 6,
        option3: 7,
        option4: 8,
        correctOption: 9,
        hint: 10,
      };

  const col = (cols, i) => (i >= 0 ? cols[i] : "") || "";

  const byRound = new Map();
  dataRows.forEach((cols, i) => {
    const roundNum = idx.round >= 0 ? parseInt(cols[idx.round], 10) : i + 1;
    if (!Number.isFinite(roundNum)) return;
    const correctRaw = parseInt(col(cols, idx.correctOption), 10);
    byRound.set(roundNum, {
      question: col(cols, idx.question),
      clue: col(cols, idx.clue),
      acceptedAnswer: col(cols, idx.acceptedAnswer),
      alsoAccept: col(cols, idx.alsoAccept),
      options: [col(cols, idx.option1), col(cols, idx.option2), col(cols, idx.option3), col(cols, idx.option4)],
      correctOption: Number.isFinite(correctRaw) ? Math.min(3, Math.max(0, correctRaw - 1)) : 0,
      hint: col(cols, idx.hint),
    });
  });

  return rounds.map((r, i) => {
    const row = byRound.get(i + 1);
    if (!row) return defaultRoundContent(r.id);
    return { roundId: r.id, ...row, approved: false };
  });
}

export function defaultCheckpointsForSetting(setting) {
  const list = DEFAULT_CHECKPOINTS_BY_SETTING[setting] || DEFAULT_CHECKPOINTS_BY_SETTING.custom;
  return list.map(({ name, location }) => ({ id: makeId("cp"), name, location }));
}

/** True if any two teams are assigned the same checkpoint in the same round. */
export function hasRouteCollisions(routes, numRounds) {
  if (!Array.isArray(routes) || routes.length === 0) return false;
  for (let r = 0; r < numRounds; r++) {
    const seen = new Set();
    for (const route of routes) {
      const cpId = route.checkpointOrder?.[r];
      if (cpId == null) continue;
      if (seen.has(cpId)) return true;
      seen.add(cpId);
    }
  }
  return false;
}

export function makeLobbyCode() {
  return String(Math.floor(100000 + Math.random() * 900000));
}

/** Same join-link shape used elsewhere in the app (e.g. ManagerDashboard's
 * buildExtramileJoinUrl / backend's buildLobbyLink): the ExtraMile guest-join page with
 * gameId/sessionId/organizationId so a new player's OTP request lands in this exact event.
 * There is no local "/join/<slug>" route in this app — a link pointing at one 404s, which was
 * the bug here. The pin travels in a `lobby` param — not a custom name like the old "lobbyPin" —
 * because that's the one the ExtraMile OTP gateway actually recognizes and echoes back as
 * `lobby` on its own login response (same convention taboo's join links use); a custom param
 * name gets silently dropped somewhere in that redirect. loginSlice.js reads it back from there
 * and resolves + joins the hunt during login itself, before the player ever sees a PIN screen. */
export function buildJoinLink({ organizationId, sessionId, lobbyPin } = {}) {
  const base = String(process.env.REACT_APP_EXTRAMILE_GUEST_JOIN_URL || "").replace(/\/$/, "");
  const gameId = String(process.env.REACT_APP_EXTRAMILE_GAME_ID || "");
  if (!base || !gameId || !organizationId || !sessionId) return "";
  const u = new URL(base);
  u.searchParams.set("gameId", gameId);
  u.searchParams.set("sessionId", String(sessionId));
  u.searchParams.set("organizationId", String(organizationId));
  if (lobbyPin) u.searchParams.set("lobby", String(lobbyPin));
  return u.toString();
}

export function routeLabelFor(index) {
  return `Route ${String.fromCharCode(65 + (index % 26))}`;
}

export function buildTeams(count, existing) {
  return Array.from({ length: count }, (_, i) => {
    const prev = existing[i];
    return (
      prev || {
        id: makeId("team"),
        name: `Team ${String(i + 1).padStart(2, "0")}`,
        route: routeLabelFor(i),
        status: "waiting",
        joinCode: Math.random().toString(36).slice(2, 8).toUpperCase(),
      }
    );
  });
}

export function labelFor(options, value) {
  return options.find((o) => o.value === value)?.label ?? value;
}

export function answerTypeLabel(value) {
  return labelFor(ANSWER_TYPE_OPTIONS, value);
}

/** Accepted file input `accept` attribute per media answer type. */
export const MEDIA_ACCEPT = {
  photo: "image/*",
  video: "video/*",
  audio: "audio/*",
  gif: "image/gif",
};

export const INSTRUCTIONS_MAX_POINTS = 6;
export const INSTRUCTIONS_MAX_CHARS = 200;
